import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"
import { useGraphite } from "@/components/Graphite"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// Graphite's buttons, as the design handoff draws them: 32px high, 0 12px, 6px corners, 13px semibold, a 16px icon 8px
// from the label. Primary is inverted, secondary a 1px control border, ghost a hover wash, danger the danger colour.
// Hover fades to 0.8; focus is a 2px outline 2px out; disabled stays visible at 40%.
const graphiteButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border font-sans text-[13px] leading-5 font-semibold cursor-pointer transition-[opacity,background-color] duration-300 ease-out-expo focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-40 aria-disabled:cursor-not-allowed aria-disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "border-0 bg-primary text-primary-foreground hover:opacity-80",
        destructive: "border-gr-danger bg-transparent text-gr-danger hover:opacity-80",
        danger: "border-0 bg-transparent text-gr-danger hover:bg-gr-hover",
        outline: "border-gr-control-border bg-transparent text-foreground hover:opacity-80",
        secondary: "border-gr-control-border bg-transparent text-foreground hover:opacity-80",
        ghost: "border-0 bg-transparent text-foreground hover:bg-gr-hover",
        link: "border-0 h-auto px-0 text-foreground underline underline-offset-2 hover:opacity-50",
      },
      size: {
        default: "h-8 px-3",
        sm: "h-7 px-2.5",
        lg: "h-9 px-4",
        icon: "h-8 w-8 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  const variants = useGraphite() ? graphiteButtonVariants : buttonVariants
  return (
    <Comp
      className={cn(variants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants, graphiteButtonVariants }
